import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { FileText, Receipt, Calendar, Landmark, Users, Truck, BookOpenCheck } from 'lucide-react';

const Workspace = () => {
  const { activeTenant, onboardingStatus } = useTenant();
  const { isGlobalAdmin, isOperator } = useAuth();
  const basePath = isGlobalAdmin() ? '/global-admin' : '/operator';
  const canOperate = isOperator() || onboardingStatus?.is_ready_to_operate === true;

  const modules = [
    {
      key: 'facturacion',
      label: 'Facturacion',
      description: 'Gestiona facturas nativas del cliente.',
      href: `${basePath}/facturacion`,
      icon: FileText,
      badge: 'Facturacion',
    },
    {
      key: 'gastos',
      label: 'Gastos',
      description: 'Registro y control de gastos del cliente.',
      href: `${basePath}/gastos`,
      icon: Receipt,
      badge: 'Gastos',
    },
    {
      key: 'cierre',
      label: 'Cierre Mensual',
      description: 'Solicita el cierre mensual y preparacion contable.',
      href: `${basePath}/cierre-mensual`,
      icon: Calendar,
      badge: 'Mensual',
    },
    {
      key: 'impuestos',
      label: 'Informes Mensuales',
      description: 'Genera balance y libro diario del periodo.',
      href: `${basePath}/impuestos`,
      icon: Landmark,
      badge: 'Informes',
    },
  ];

  const catalogs = [
    {
      key: 'catalog-clients',
      label: 'Clientes a facturar',
      description: 'Catalogo de clientes registrados.',
      href: `${basePath}/catalogos/clientes`,
      icon: Users,
      badge: 'Clientes',
    },
    {
      key: 'catalog-vendors',
      label: 'Proveedores',
      description: 'Catalogo de proveedores y gastos.',
      href: `${basePath}/catalogos/proveedores`,
      icon: Truck,
      badge: 'Proveedores',
    },
    {
      key: 'catalog-tax',
      label: 'Modelos de impuestos',
      description: 'Configura modelos fiscales recurrentes.',
      href: `${basePath}/catalogos/modelos`,
      icon: BookOpenCheck,
      badge: 'Modelos',
    },
  ];

  if (!activeTenant) {
    return (
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-heading font-bold mb-2">Workspace Operador</h1>
        <p className="text-muted-foreground">Selecciona un cliente para empezar.</p>
      </div>
    );
  }

  return (
    <div data-testid="operator-workspace" className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-bold">Workspace Operador</h1>
        <p className="text-muted-foreground">
          Accede a los modulos operativos del cliente seleccionado.
        </p>
      </div>

      {!canOperate && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          La cuenta del cliente esta en revision. Puedes revisar la informacion, pero evita crear movimientos productivos.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((module) => {
          const Icon = module.icon;
          return (
            <Link key={module.key} to={module.href}>
              <Card className="h-full hover:border-primary/60 hover:shadow-md transition-all">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <Badge variant="secondary">{module.badge}</Badge>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{module.label}</h3>
                    <p className="text-sm text-muted-foreground">{module.description}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="pt-2">
        <h2 className="text-xl font-heading font-semibold mb-3">Catalogos</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {catalogs.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.key} to={item.href}>
                <Card className="h-full hover:border-primary/60 hover:shadow-md transition-all">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <Badge variant="secondary">{item.badge}</Badge>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{item.label}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Workspace;
